import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { starsApi, type StarDiscovery } from '../api/stars';
import { useToastStore } from '../stores/toastStore';

export function useStarMap() {
  return useQuery({
    queryKey: ['stars', 'map'],
    queryFn: () => starsApi.getMap().then((res) => res.data.data),
    staleTime: 30000,
  });
}

export function useUniversePlanets() {
  return useQuery({
    queryKey: ['stars', 'planets'],
    queryFn: () => starsApi.getPlanets().then((res) => res.data.data.planets),
    staleTime: 30000,
  });
}

export function useScanStar() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation<StarDiscovery, any, number>({
    mutationFn: (index: number) => starsApi.scanStar(index).then((res) => res.data.data.discovery),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['stars', 'map'] });
      queryClient.invalidateQueries({ queryKey: ['planet', 'me'] });
      const planetMsg = data.hasPlanet ? ` — civilization detected!` : '';
      addToast(`Discovered ${data.starType} at ${data.distance.toFixed(1)} ly!${planetMsg}`, 'success');
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error?.message || 'Scan failed';
      addToast(msg, 'error');
    },
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation({
    mutationFn: ({ index, templateKey, customText }: { index: number; templateKey: string; customText?: string }) =>
      starsApi.sendMessage(index, templateKey, customText).then((res) => res.data.data.message),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['stars', 'map'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['planet', 'me'] });
      addToast(`Message launched! Arrives in ${data.travelDays} day${data.travelDays !== 1 ? 's' : ''}.`, 'success');
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error?.message || 'Message failed';
      addToast(msg, 'error');
    },
  });
}
